<i18n locale="en">
{
  "title": "Image Preview",
  "setAsPrimary": "Set as Primary",
  "imageAlt": "Preview"
}
</i18n>

<template>
  <OwnDialog v-model="isVisible" size="lg" @cancel="onClose">
    <template #heading>
      <OwnDialogHeading :title="t('title')" @cancel="onClose" />
    </template>

    <OwnDialogContent>
      <div v-if="media && media.url" class="image-preview">
        <img :src="media.url" :alt="t('imageAlt')" class="preview-image" />
      </div>
    </OwnDialogContent>

    <template #footer>
      <OwnDialogFooter>
        <OwnButton
          :text="t('setAsPrimary')"
          variant="primary"
          :processing="isPromoting"
          @click="onSetAsPrimaryClick"
        />
      </OwnDialogFooter>
    </template>
  </OwnDialog>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnButton } from '@/ui/OwnButton'
import {
  OwnDialog,
  OwnDialogContent,
  OwnDialogFooter,
  OwnDialogHeading,
} from '@/ui/OwnDialog'

import type { Media } from './types'

interface Props {
  modelValue: boolean
  media: Media | null
  isPromoting: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'set-as-primary'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)
  },
})

/**
 * Closes the preview overlay
 */
function onClose(): void {
  emit('update:modelValue', false)
}

/**
 * Handles clicking the Set as Primary button
 */
function onSetAsPrimaryClick(): void {
  emit('set-as-primary')
}
</script>

<style scoped>
.image-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.preview-image {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
}
</style>
